/**
 * @description 埋点来源上下文：source_type / source_id / source_name / from_page
 * @reason 监控侧会把 properties 中的 source_* 提升为顶层可筛字段；商详需记录入口来源
 */

import { storageKey, STORAGE_NAMES } from '~/utils/storageKey'

/** @type {Record<string, string>} 常见来源类型（与监控文档对齐，可扩展） */
export const TRACK_SOURCE_TYPE = {
  MALL_HOMEPAGE: 'mall_homepage',
  MALL_BANNER: 'mall_banner',
  MALL_LIST: 'mall_list',
  ACTIVITY_PAGE: 'activity_page',
  CATEGORY_HOMEPAGE: 'category_homepage',
  MEGA_MENU: 'mega_menu',
  PRODUCT_DETAIL: 'product_detail',
  SEARCH_RESULT: 'search_result',
  SCHEME_DETAIL: 'scheme_detail',
  HOMEPAGE_CARD: 'homepage_card',
  HOMEPAGE_BANNER: 'homepage_banner',
  CART: 'cart',
  CONFIRM_PAGE: 'confirm_page',
}

const ENTRY_SOURCE_TTL_MS = 30 * 60 * 1000
const ENTRY_SOURCE_MAX = 40

/**
 * @description 归一化来源字段
 * @param {Record<string, any>} [input]
 * @returns {{ sourceType: string, sourceId: string, sourceName: string, fromPage: string, properties: Record<string, string> }}
 */
export function buildTrackSourceFields(input = {}) {
  const sourceType = String(
    input.sourceType ?? input.source_type ?? input.fromPage ?? input.from_page ?? ''
  ).trim()
  const sourceId = String(input.sourceId ?? input.source_id ?? '').trim()
  const sourceName = String(input.sourceName ?? input.source_name ?? '').trim()
  const fromPage = String(input.fromPage ?? input.from_page ?? sourceType ?? '').trim()

  const properties = {}
  if (sourceType) properties.source_type = sourceType
  if (sourceId) properties.source_id = sourceId
  if (sourceName) properties.source_name = sourceName
  if (fromPage) properties.from_page = fromPage

  return {
    sourceType,
    sourceId,
    sourceName,
    fromPage,
    properties,
  }
}

/**
 * @description 从页面 trackContext + 商品对象解析来源（活动商品优先用 promotionId）
 * @param {Record<string, any>|null|undefined} trackContext
 * @param {Record<string, any>|null|undefined} [product]
 * @returns {{ sourceType: string, sourceId: string, sourceName: string, fromPage: string, properties: Record<string, string> }}
 */
export function resolveTrackSourceFromContext(trackContext = {}, product = null) {
  const ctx = trackContext && typeof trackContext === 'object' ? trackContext : {}
  const base = buildTrackSourceFields({
    sourceType: ctx.sourceType || ctx.fromPage,
    sourceId: ctx.sourceId,
    sourceName: ctx.sourceName || ctx.pageName,
    fromPage: ctx.fromPage || ctx.sourceType,
  })

  if (!product || typeof product !== 'object') return base

  if (base.sourceType === TRACK_SOURCE_TYPE.ACTIVITY_PAGE) {
    const actId = product.promotionId ?? product.activityId ?? product.activity_id
    if (actId !== undefined && actId !== null && String(actId).trim() !== '') {
      base.sourceId = String(actId).trim()
    }
    const actName = String(
      product.activityName ||
        product.promotionName ||
        product.packageName ||
        product.campaignName ||
        ''
    ).trim()
    if (actName) base.sourceName = actName
  }

  if (
    (base.sourceType === TRACK_SOURCE_TYPE.CATEGORY_HOMEPAGE ||
      base.sourceType === TRACK_SOURCE_TYPE.MALL_LIST) &&
    !base.sourceId
  ) {
    const categoryId = product.categoryId ?? product.category?.id
    if (categoryId !== undefined && categoryId !== null && String(categoryId).trim() !== '') {
      base.sourceId = String(categoryId).trim()
    }
  }

  base.properties = buildTrackSourceFields(base).properties
  return base
}

/**
 * @description 从 URL 中解析商城商品 ID（用于 Banner / 外链进入商详记来源）
 * @param {string} url
 * @returns {string}
 */
export function extractStoreProductIdFromUrl(url) {
  const raw = String(url || '').trim()
  if (!raw) return ''
  try {
    const path = /^https?:\/\//i.test(raw) ? new URL(raw).pathname : raw.split(/[?#]/)[0]
    const matched = path.match(/\/store\/products\/([^/]+)/i)
    return matched ? decodeURIComponent(matched[1]) : ''
  } catch {
    const matched = raw.match(/\/store\/products\/([^/?#]+)/i)
    return matched ? matched[1] : ''
  }
}

function getEntrySourceStorageKey() {
  return storageKey(STORAGE_NAMES.TRACK_PRODUCT_ENTRY_SOURCE)
}

function readEntrySourceMap() {
  if (!import.meta.client) return {}
  try {
    const raw = sessionStorage.getItem(getEntrySourceStorageKey())
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

function writeEntrySourceMap(map) {
  if (!import.meta.client) return
  try {
    sessionStorage.setItem(getEntrySourceStorageKey(), JSON.stringify(map))
  } catch {
    // ignore quota / private mode
  }
}

/**
 * @description 记录进入商详的来源（按 prodId；新标签页可共享 sessionStorage）
 * @param {string|number} prodId
 * @param {Record<string, any>} source
 * @returns {void}
 */
export function rememberProductEntrySource(prodId, source) {
  if (!import.meta.client) return
  const id = String(prodId || '').trim()
  if (!id) return
  const fields = buildTrackSourceFields(source)
  if (!fields.sourceType) return

  const map = readEntrySourceMap()
  const now = Date.now()
  Object.keys(map).forEach((key) => {
    const item = map[key]
    if (!item?.rememberedAt || now - Number(item.rememberedAt) > ENTRY_SOURCE_TTL_MS) {
      delete map[key]
    }
  })

  map[id] = {
    sourceType: fields.sourceType,
    sourceId: fields.sourceId,
    sourceName: fields.sourceName,
    fromPage: fields.fromPage,
    rememberedAt: now,
  }

  const keys = Object.keys(map)
  if (keys.length > ENTRY_SOURCE_MAX) {
    keys
      .sort((a, b) => Number(map[a]?.rememberedAt || 0) - Number(map[b]?.rememberedAt || 0))
      .slice(0, keys.length - ENTRY_SOURCE_MAX)
      .forEach((key) => {
        delete map[key]
      })
  }

  writeEntrySourceMap(map)
}

/**
 * @description 读取商详入口来源（不清除，供同页多次事件复用）
 * @param {string|number} prodId
 * @returns {{ sourceType: string, sourceId: string, sourceName: string, fromPage: string, properties: Record<string, string> }}
 */
export function peekProductEntrySource(prodId) {
  const id = String(prodId || '').trim()
  if (!id || !import.meta.client) return buildTrackSourceFields()
  const map = readEntrySourceMap()
  const item = map[id]
  if (!item) return buildTrackSourceFields()
  if (Date.now() - Number(item.rememberedAt || 0) > ENTRY_SOURCE_TTL_MS) {
    delete map[id]
    writeEntrySourceMap(map)
    return buildTrackSourceFields()
  }
  return buildTrackSourceFields(item)
}

/**
 * @description 从路由 query 解析来源（兼容显式传参）
 * @param {Record<string, any>|null|undefined} query
 * @returns {{ sourceType: string, sourceId: string, sourceName: string, fromPage: string, properties: Record<string, string> }}
 */
export function resolveTrackSourceFromRouteQuery(query = {}) {
  const q = query && typeof query === 'object' ? query : {}
  return buildTrackSourceFields({
    sourceType: q.sourceType || q.source_type || q.from || q.fromPage || q.from_page,
    sourceId: q.sourceId || q.source_id,
    sourceName: q.sourceName || q.source_name,
    fromPage: q.fromPage || q.from_page || q.from || q.sourceType || q.source_type,
  })
}
